import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Send, User, Bot, Users as UsersIcon, CheckCircle, Clock as ClockIcon } from 'lucide-react';

const SupportChat = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const messagesEndRef = useRef(null);
  const selectedRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.getAllConversations();
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getSupportStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await api.getConversationMessages(conversationId);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchStats();
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedRef.current) fetchMessages(selectedRef.current.id);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchStats, fetchMessages]);

  useEffect(() => {
    selectedRef.current = selectedConversation;
    if (selectedConversation) fetchMessages(selectedConversation.id);
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    
    // Assign conversation if not already assigned
    if (!conv.assigned_to) {
      try {
        await api.assignConversation(conv.id);
        fetchConversations();
      } catch (error) {
        console.error('Failed to assign conversation:', error);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedConversation) return;

    const message = inputMessage;
    setInputMessage('');

    try {
      await api.sendSupportMessage({
        conversation_id: selectedConversation.id,
        message: message
      });

      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Kunde inte skicka meddelande');
    }
  };

  const handleResolve = async (conversationId) => {
    try {
      await api.updateConversationStatus(conversationId, 'resolved');
      toast.success('Konversation markerad som löst');
      fetchConversations();
      setSelectedConversation(null);
    } catch (error) {
      console.error('Failed to resolve conversation:', error);
      toast.error('Kunde inte uppdatera status');
    }
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'ai':
        return <Bot className="text-orange-500" size={20} />;
      case 'support':
        return <UsersIcon className="text-green-500" size={20} />;
      default:
        return <User className="text-muted-foreground" size={20} />;
    }
  };

  const openConversations = conversations.filter(c => c.status === 'open');
  const resolvedConversations = conversations.filter(c => c.status === 'resolved');

  if (loading) {
    return <div>Laddar...</div>;
  }

  return (
    <div className="space-y-6" data-testid="support-chat-page">
      <div>
        <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Work Sans' }}>Support Chat</h2>
        <p className="text-muted-foreground mt-1">Hantera kundkonversationer</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <MessageCircle className="text-orange-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Öppna konversationer</p>
                <p className="text-2xl font-bold text-foreground">{stats.open_conversations}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tilldelade</p>
                <p className="text-2xl font-bold text-foreground">{stats.assigned_conversations}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${stats.support_available ? 'bg-green-100' : 'bg-muted'}`}>
                <ClockIcon className={stats.support_available ? 'text-green-500' : 'text-muted-foreground'} size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Support-status</p>
                <p className="text-lg font-semibold text-foreground">
                  {stats.support_available ? 'Tillgänglig' : 'Ej tillgänglig'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Interface */}
      <div className="grid grid-cols-12 gap-6 h-[600px]">
        {/* Conversations List */}
        <div className="col-span-4 bg-card rounded-xl shadow-sm border border-border overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border bg-muted/50">
            <h3 className="font-semibold text-foreground">Konversationer</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {openConversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <MessageCircle className="mx-auto mb-3 text-muted-foreground/40" size={40} />
                <p className="text-sm">Inga öppna konversationer</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {openConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium text-foreground">{conv.user_name || 'Användare'}</p>
                      {conv.assigned_to && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Tilldelad</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {new Date(conv.last_message_at).toLocaleString('sv-SE')}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Messages Panel */}
        <div className="col-span-8 bg-card rounded-xl shadow-sm border border-border flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-border bg-muted/50 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-foreground">{selectedConversation.user_name || 'Användare'}</h3>
                  <p className="text-sm text-muted-foreground">Konversation #{selectedConversation.id.slice(0, 8)}</p>
                </div>
                <Button
                  onClick={() => handleResolve(selectedConversation.id)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="resolve-conversation-btn"
                >
                  <CheckCircle size={18} className="mr-2" />
                  Markera som löst
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.sender_type === 'support' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      {getSenderIcon(msg.sender_type)}
                    </div>
                    <div className={`flex-1 ${msg.sender_type === 'support' ? 'text-right' : ''}`}>
                      <p className="text-xs text-muted-foreground mb-1">{msg.sender_name || msg.sender_type}</p>
                      <div
                        className={`inline-block px-4 py-2 rounded-2xl max-w-[85%] ${
                          msg.sender_type === 'support'
                            ? 'bg-green-500 text-white'
                            : msg.sender_type === 'ai'
                            ? 'bg-orange-100 dark:bg-orange-900/20 text-foreground border border-orange-200'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString('sv-SE', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-border">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Skriv ditt svar..."
                    data-testid="support-message-input"
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={!inputMessage.trim()}
                    className="bg-green-600 hover:bg-green-700 px-4"
                    data-testid="send-support-message-btn"
                  >
                    <Send size={18} />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground/60">
              <div className="text-center">
                <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                <p>Välj en konversation för att svara</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resolved Conversations */}
      {resolvedConversations.length > 0 && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Lösta konversationer ({resolvedConversations.length})</h3>
          <div className="space-y-2">
            {resolvedConversations.slice(0, 5).map((conv) => (
              <div key={conv.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div>
                  <p className="font-medium text-foreground">{conv.user_name || 'Användare'}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(conv.last_message_at).toLocaleString('sv-SE')}
                  </p>
                </div>
                <CheckCircle className="text-green-500" size={20} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportChat;
